// Google Analytics 4 tracking service
import logger from '../utils/logger';

// Get GA Measurement ID from environment variables
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

// Initialize Google Analytics
export const initGA = () => {
  // Skip initialization in development if no GA ID is provided
  if (process.env.NODE_ENV === 'development' && !GA_MEASUREMENT_ID) {
    logger.analytics('Google Analytics disabled in development environment');
    // Create no-op function to prevent errors
    window.gtag = () => {};
    return;
  }

  if (!GA_MEASUREMENT_ID) {
    logger.warn('REACT_APP_GA_MEASUREMENT_ID is not set. Google Analytics tracking will be disabled.');
    // Create no-op function to prevent errors
    window.gtag = () => {};
    return;
  }

  try {
    logger.analytics('Initializing Google Analytics with ID:', GA_MEASUREMENT_ID);

    // Load gtag script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    
    // Add error handling for script loading
    script.onerror = (error) => {
      logger.warn('Google Analytics script failed to load:', error);
      window.gtag = () => {}; // Create no-op function
    };
    
    document.head.appendChild(script);

    // Initialize gtag with error handling
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      try {
        window.dataLayer.push(arguments);
      } catch (error) {
        logger.warn('Google Analytics tracking error:', error);
      }
    }
    
    gtag('js', new Date());
    gtag('config', GA_MEASUREMENT_ID, {
      page_title: document.title,
      page_location: window.location.href,
      // Disable automatic page view tracking to prevent network errors
      send_page_view: false,
    });

    // Make gtag available globally
    window.gtag = gtag;
  } catch (error) {
    logger.warn('Failed to initialize Google Analytics:', error);
    // Create no-op function to prevent errors
    window.gtag = () => {};
  }
};

// Track page views
export const trackPageView = (page_path, page_title) => {
  if (!GA_MEASUREMENT_ID || !window.gtag) {
    return;
  }

  try {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path,
      page_title,
    });
  } catch (error) {
    logger.warn('Error tracking page view:', error);
  }
};

// Track custom events
export const trackEvent = (eventName, parameters = {}) => {
  if (!GA_MEASUREMENT_ID || !window.gtag) {
    return;
  }

  try {
    window.gtag('event', eventName, {
      event_category: 'Tarot App',
      ...parameters,
    });
  } catch (error) {
    logger.warn('Error tracking event:', eventName, error);
  }
};

// Predefined event trackers for common tarot app actions
export const analytics = {
  // Generic track event function
  trackEvent,

  // Track when user draws cards for a spread
  trackCardDraw: (spreadType, cardsDrawn) => {
    trackEvent('card_draw', {
      event_category: 'Tarot Reading',
      spread_type: spreadType,
      cards_drawn: cardsDrawn,
      value: cardsDrawn,
    });
  },

  // Track when spread drawing is completed
  trackSpreadDrawn: (spreadType, cardCount) => {
    // Create event name with spread type appended
    const eventName = `spread_drawn_${spreadType}`;
    trackEvent(eventName, {
      event_category: 'Tarot Reading',
      spread_type: spreadType,
      card_count: cardCount,
    });
  },

  // Track when user saves a reading to diary
  trackSaveReading: (spreadType, readingId) => {
    trackEvent('save_reading', {
      event_category: 'Diary',
      spread_type: spreadType,
      reading_id: readingId,
    });
  },

  // Track when user views diary
  trackViewDiary: () => {
    trackEvent('view_diary', {
      event_category: 'Navigation',
    });
  },

  // Track when user imports a reading
  trackImportReading: () => {
    trackEvent('import_reading', {
      event_category: 'Diary',
    });
  },

  // Track when user deletes a reading
  trackDeleteReading: (readingId) => {
    trackEvent('delete_reading', {
      event_category: 'Diary',
      reading_id: readingId,
    });
  },

  // Track when user views reading details
  trackViewReading: (readingId, spreadType) => {
    trackEvent('view_reading_detail', {
      event_category: 'Reading',
      reading_id: readingId,
      spread_type: spreadType,
    });
  },

  // Track spread type selection
  trackSpreadSelection: (spreadType) => {
    trackEvent('spread_selection', {
      event_category: 'Navigation',
      spread_type: spreadType,
    });
  },

  // Track user engagement time
  trackEngagement: (timeSpentSeconds, page) => {
    trackEvent('engagement', {
      event_category: 'User Engagement',
      engagement_time_msec: timeSpentSeconds * 1000,
      page_title: page,
    });
  },

  // Track user authentication events
  trackUserRegistration: (method = 'email') => {
    trackEvent('sign_up', {
      event_category: 'Authentication',
      method: method,
    });
  },

  trackUserLogin: (method = 'email') => {
    trackEvent('login', {
      event_category: 'Authentication',
      method: method,
    });
  },

  trackUserLogout: () => {
    trackEvent('logout', {
      event_category: 'Authentication',
    });
  },

  // Track user profile actions
  trackProfileUpdate: () => {
    trackEvent('profile_update', {
      event_category: 'User Management',
    });
  },

  trackPasswordChange: () => {
    trackEvent('password_change', {
      event_category: 'Security',
    });
  },

  trackPasswordReset: (action) => {
    trackEvent(`password_reset_${action}`, {
      event_category: 'Security',
      action: action, // 'request' or 'complete'
    });
  },

  // Track diary synchronization
  trackDiarySync: (itemsSynced) => {
    trackEvent('diary_sync', {
      event_category: 'Data Management',
      items_synced: itemsSynced,
      value: itemsSynced,
    });
  },

  // Track offline usage
  // Track offline usage
  trackOfflineUsage: (action) => {
    trackEvent('offline_usage', {
      event_category: 'User Experience',
      action: action, // 'backup_created', 'sync_attempted', etc.
    });
  },

  // === PAYMENT & MONETIZATION TRACKING ===
  
  // Track payment modal open/close
  trackPaymentModalOpen: (spreadType, trigger) => {
    trackEvent('payment_modal_open', {
      event_category: 'Payment',
      spread_type: spreadType,
      trigger: trigger, // 'ai_explanation_button', 'ask_taroca_button', etc.
    });
  },

  trackPaymentModalClose: (stage) => {
    trackEvent('payment_modal_close', {
      event_category: 'Payment',
      stage: stage, // 'before_payment', 'after_payment', 'after_success'
    });
  },

  // Track payment initialization
  trackPaymentInitialization: (hasAppliedCoupon, couponCode) => {
    trackEvent('payment_initialization', {
      event_category: 'Payment',
      has_applied_coupon: hasAppliedCoupon,
      coupon_code: couponCode || 'none',
    });
  },

  // Track payment processing
  trackPaymentStart: (amount, appliedCoupon) => {
    trackEvent('payment_start', {
      event_category: 'Payment',
      value: amount / 100, // Convert cents to dollars
      currency: 'USD',
      applied_coupon: appliedCoupon?.code || 'none',
      final_amount: appliedCoupon?.finalAmount ? appliedCoupon.finalAmount / 100 : amount / 100,
    });
  },

  trackPaymentSuccess: (amount, appliedCoupon, paymentId) => {
    trackEvent('purchase', { // Standard GA4 e-commerce event
      event_category: 'Payment',
      transaction_id: paymentId,
      value: appliedCoupon?.finalAmount ? appliedCoupon.finalAmount / 100 : amount / 100,
      currency: 'USD',
      items: [{
        item_id: 'ai_explanation',
        item_name: 'Tarot AI Explanation',
        category: 'Digital Service',
        quantity: 1,
        price: appliedCoupon?.finalAmount ? appliedCoupon.finalAmount / 100 : amount / 100,
      }],
      applied_coupon: appliedCoupon?.code || 'none',
    });
  },

  trackPaymentError: (error, stage, amount) => {
    trackEvent('payment_error', {
      event_category: 'Payment Error',
      error_message: error.substring(0, 100), // Limit error message length
      stage: stage, // 'initialization', 'processing', 'completion'
      amount: amount ? amount / 100 : 0,
    });
  },

  // Track coupon usage
  trackCouponFieldOpen: () => {
    trackEvent('coupon_field_open', {
      event_category: 'Coupon',
    });
  },

  trackCouponApplied: (couponCode, discountAmount, discountType, finalAmount) => {
    trackEvent('coupon_applied', {
      event_category: 'Coupon',
      coupon_code: couponCode,
      discount_amount: discountAmount / 100,
      discount_type: discountType,
      final_amount: finalAmount / 100,
    });
  },

  trackCouponError: (couponCode, errorMessage) => {
    trackEvent('coupon_error', {
      event_category: 'Coupon Error',
      coupon_code: couponCode,
      error_message: errorMessage.substring(0, 100),
    });
  },

  trackFreeAccessGranted: (couponCode, trigger) => {
    trackEvent('free_access_granted', {
      event_category: 'Monetization',
      coupon_code: couponCode || 'auto_applied',
      trigger: trigger, // 'first_time_coupon', 'manual_coupon', etc.
    });
  },

  // Track AI explanation generation (enhanced)
  trackAIExplanationStart: (spreadType, paymentType) => {
    trackEvent('ai_explanation_start', {
      event_category: 'AI Service',
      spread_type: spreadType,
      payment_type: paymentType, // 'paid', 'free_coupon', 'first_time_free'
    });
  },

  trackAIExplanationComplete: (spreadType, paymentType, generationTimeMs) => {
    trackEvent('ai_explanation_complete', {
      event_category: 'AI Service',
      spread_type: spreadType,
      payment_type: paymentType,
      generation_time_ms: generationTimeMs,
      value: generationTimeMs / 1000, // Convert to seconds for value
    });
  },

  trackAIExplanationError: (spreadType, paymentType, error) => {
    trackEvent('ai_explanation_error', {
      event_category: 'AI Service Error',
      spread_type: spreadType,
      payment_type: paymentType,
      error_message: error.substring(0, 100),
    });
  },

  // Track post-payment actions
  trackViewReadingAfterPayment: (readingId, timeToView) => {
    trackEvent('view_reading_after_payment', {
      event_category: 'Post Payment',
      reading_id: readingId,
      time_to_view_seconds: Math.round(timeToView / 1000),
      value: Math.round(timeToView / 1000),
    });
  },

  trackViewDiaryAfterPayment: (timeToView) => {
    trackEvent('view_diary_after_payment', {
      event_category: 'Post Payment',
      time_to_view_seconds: Math.round(timeToView / 1000),
    });
  },

  // Track conversion funnel
  trackConversionFunnel: (step, spreadType, userType) => {
    trackEvent('conversion_funnel', {
      event_category: 'Conversion',
      funnel_step: step, // 'spread_drawn', 'ai_button_clicked', 'payment_modal_opened', 'payment_completed'
      spread_type: spreadType,
      user_type: userType, // 'new', 'returning', 'premium'
    });
  },

  // Track page views (alias for consistency)
  trackPageView: (page_path, page_title) => {
    trackPageView(page_path, page_title);
  },
};

export default analytics;
